<template>
  <div>
    <wz-card title="Plantillas">
      <p class="text-muted text-sm mb-3">
        Deja en "Cualquiera" lo que no cambia. Para un colaborador gana la plantilla que más dimensiones calce;
        en empate manda cargo, luego área, tipo de contrato, sede y empresa. Subir otra plantilla para la misma
        combinación crea su versión siguiente: la anterior queda inactiva y se puede seguir descargando.
      </p>

      <div class="mb-3">
        <label class="wz-form-label">Tipo de plantilla</label>
        <div class="flex gap-4">
          <label class="flex items-center gap-2 cursor-pointer">
            <input type="radio" v-model="form.templateKind" value="MERGE" @change="resetFile" />
            <span>Con variables: un Word con marcadores, que se llena y se entrega en PDF</span>
          </label>
          <label class="flex items-center gap-2 cursor-pointer">
            <input type="radio" v-model="form.templateKind" value="FIXED" @change="resetFile" />
            <span>Archivo fijo: un PDF que se entrega tal cual (reglamentos, políticas)</span>
          </label>
        </div>
      </div>

      <!-- Carga -->
      <div class="flex flex-wrap -mx-2">
        <div class="w-full md:w-1/3 mb-3 px-2">
          <label class="wz-form-label">Empresa</label>
          <wz-select v-model="form.companyId" :items="companyOptions" defaultOption="Cualquiera" @change="onCompanyChange" />
        </div>
        <div class="w-full md:w-1/3 mb-3 px-2">
          <label class="wz-form-label">Sede</label>
          <wz-select v-model="form.siteId" :items="siteOptions" defaultOption="Cualquiera" :disabled="!form.companyId" />
        </div>
        <div class="w-full md:w-1/3 mb-3 px-2">
          <label class="wz-form-label">Tipo de contrato</label>
          <wz-select v-model="form.contractType" :items="contractTypeOptions" defaultOption="Cualquiera" />
        </div>
        <div class="w-full md:w-1/3 mb-3 px-2">
          <label class="wz-form-label">Área</label>
          <wz-select v-model="form.areaId" :items="areaOptions" defaultOption="Cualquiera" />
        </div>
        <div class="w-full md:w-1/3 mb-3 px-2">
          <label class="wz-form-label">Cargo</label>
          <wz-select v-model="form.positionId" :items="positionOptions" defaultOption="Cualquiera" />
        </div>
        <div class="w-full md:w-1/3 mb-3 px-2"></div>

        <!-- Control de versión que el cliente ya lleva en el encabezado de sus documentos -->
        <div class="w-full md:w-1/4 mb-3 px-2">
          <wz-input v-model="form.externalCode" label="Código del documento" placeholder="Opcional" />
        </div>
        <div class="w-full md:w-1/4 mb-3 px-2">
          <wz-input v-model="form.externalVersion" label="Versión del documento" placeholder="Opcional" />
        </div>
        <div class="w-full md:w-1/4 mb-3 px-2">
          <wz-input v-model="form.approvedBy" label="Aprobado por" placeholder="Opcional" />
        </div>
        <div class="w-full md:w-1/4 mb-3 px-2">
          <label class="wz-form-label">Fecha de aprobación</label>
          <input v-model="form.approvedAt" type="date" class="wz-form-control w-full" />
        </div>

        <div class="w-full mb-3 px-2">
          <template v-if="form.templateKind === 'FIXED'">
            <label class="wz-form-label">Archivo PDF <small class="text-muted">(se entrega exactamente como lo subes)</small></label>
            <wz-file-input :key="fileKey" id-file="document-template-file" :accept-file="['.pdf']" @onChange="onFileChange" />
          </template>
          <template v-else>
            <label class="wz-form-label">Archivo DOCX <small class="text-muted">(con marcadores {{ '${grupo.campo}' }}; fuera de los cuadros de texto)</small></label>
            <wz-file-input :key="fileKey" id-file="document-template-file" :accept-file="['.docx']" @onChange="onFileChange" />
          </template>
        </div>
      </div>
      <div class="flex justify-end">
        <wz-button variant="primary" :disabled="!file || uploading" @click="upload(false)">
          {{ uploading ? 'Subiendo...' : 'Subir plantilla' }}
        </wz-button>
      </div>
    </wz-card>

    <!-- Versiones -->
    <wz-table-container class="mt-3">
      <wz-table>
        <thead>
          <tr>
            <th>Aplica a</th>
            <th>Versión</th>
            <th>Tipo</th>
            <th>Código / versión del cliente</th>
            <th>Archivo</th>
            <th>Cargada</th>
            <th>Estado</th>
            <th>Opciones</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="templates.length === 0">
            <td colspan="8" class="text-center text-muted py-4">Este tipo de documento aún no tiene plantillas.</td>
          </tr>
          <tr v-for="t in templates" :key="t.id">
            <td>{{ scopeLabel(t) }}</td>
            <td>v{{ t.versionNumber }}</td>
            <td>{{ t.templateKind === 'FIXED' ? 'Archivo fijo' : 'Con variables' }}</td>
            <td>{{ externalLabel(t) }}</td>
            <td>{{ t.fileName }}</td>
            <td>{{ formatDate(t.createdAt) }}<br /><small class="text-muted">{{ t.createdBy }}</small></td>
            <td>
              <wz-badge :variant="t.isActive ? 'success' : 'secondary'" pill>
                {{ t.isActive ? 'Activa' : 'Inactiva' }}
              </wz-badge>
            </td>
            <td style="text-align: center;">
              <wz-dropdown :items="rowActions(t)" placement="left" @click="handleAction(t, $event)" />
            </td>
          </tr>
        </tbody>
      </wz-table>
    </wz-table-container>
  </div>
</template>

<script>
import { Alert, Util } from '@wizepe/wizeui'
import fileDownload from 'js-file-download'
import { DocumentTypeService, CompanyService, WorkSiteService, AreaService, PositionService } from '@/core/services'
import { Constants } from '@/core/utils'

const emptyForm = () => ({
  companyId: null,
  siteId: null,
  areaId: null,
  positionId: null,
  contractType: null,
  externalCode: null,
  externalVersion: null,
  approvedBy: null,
  approvedAt: null,
  templateKind: 'MERGE',
})

export default {
  name: 'DocumentTemplatesSection',
  props: {
    documentTypeId: { type: Number, required: true },
  },
  data() {
    return {
      templates: [],
      companies: [],
      sites: [],
      areas: [],
      positions: [],
      contractTypeOptions: Object.values(Constants.ContractType).map(t => ({ key: t.key, label: t.label })),
      form: emptyForm(),
      file: null,
      fileKey: 0,
      uploading: false,
    }
  },
  computed: {
    companyOptions() { return this.companies.map(c => ({ key: c.id, label: c.name })) },
    siteOptions() { return this.sites.map(s => ({ key: s.id, label: s.name })) },
    areaOptions() { return this.areas.map(a => ({ key: a.id, label: a.description })) },
    positionOptions() { return this.positions.map(p => ({ key: p.id, label: p.description || p.name })) },
  },
  async created() {
    await Promise.all([this.loadCatalogs(), this.load()])
  },
  methods: {
    async loadCatalogs() {
      try {
        const [comp, area, pos] = await Promise.all([
          CompanyService.listAll(), AreaService.listActive(), PositionService.listAll(),
        ])
        this.companies = comp.payload || []
        this.areas = area.payload || []
        this.positions = pos.payload || []
      } catch (e) {
        console.error('Error cargando catálogos', e)
      }
    },
    async onCompanyChange() {
      this.form.siteId = null
      this.sites = []
      if (!this.form.companyId) return
      try {
        const resp = await WorkSiteService.listByCompanyAndStatus(this.form.companyId, 1)
        this.sites = resp.payload || []
      } catch (e) {
        console.error('Error cargando sedes', e)
      }
    },
    async load() {
      try {
        const resp = await DocumentTypeService.templates(this.documentTypeId)
        this.templates = resp.payload || []
      } catch (e) {
        Alert.error(e.errors ? e.errors.message : 'Error al cargar las plantillas')
      }
    },
    onFileChange(files) {
      this.file = files && files.length > 0 ? files[0] : null
    },
    resetFile() {
      this.file = null
      this.fileKey++
    },
    async upload(acknowledgeImages = false) {
      if (!this.file) return
      const fd = new FormData()
      Object.entries(this.form).forEach(([k, v]) => {
        if (v !== null && v !== undefined && v !== '') fd.append(k, v)
      })
      if (acknowledgeImages === true) fd.append('acknowledgeImages', 'true')
      fd.append('file', this.file)
      try {
        this.uploading = true
        const resp = await DocumentTypeService.uploadTemplate(this.documentTypeId, fd)
        const v = resp.payload ? resp.payload.versionNumber : null
        Alert.success(v && v > 1 ? `Se cargó la versión ${v}; la anterior quedó inactiva` : 'Plantilla cargada')
        this.form = emptyForm()
        this.sites = []
        this.file = null
        this.fileKey++
        this.load()
      } catch (e) {
        const message = e.errors ? e.errors.message : 'Error al subir la plantilla'
        // Una plantilla con imágenes pregunta antes de cargarse: podría traer una firma.
        if (message.includes('imagen(es)')) {
          Alert.dialog('¿Trae una firma?', message, () => this.upload(true))
        } else {
          Alert.error(message)
        }
      } finally {
        this.uploading = false
      }
    },
    rowActions(t) {
      const actions = [{ key: 'download', label: 'Descargar DOCX' }]
      if (t.isActive) actions.push({ key: 'disable', label: 'Desactivar' })
      return actions
    },
    handleAction(t, item) {
      if (item.key === 'download') this.download(t)
      if (item.key === 'disable') this.confirmDisable(t)
    },
    async download(t) {
      try {
        const resp = await DocumentTypeService.downloadTemplate(this.documentTypeId, t.id)
        fileDownload(resp.data, Util.getFileName(resp) || t.fileName || `plantilla-v${t.versionNumber}.docx`)
      } catch (e) {
        Alert.error('Error al descargar el DOCX')
      }
    },
    confirmDisable(t) {
      Alert.dialog('Desactivar',
        `¿Desactivar la versión ${t.versionNumber} (${this.scopeLabel(t)})? Se conserva y se puede seguir descargando.`,
        async () => {
          try {
            await DocumentTypeService.deactivateTemplate(this.documentTypeId, t.id)
            Alert.success('Plantilla desactivada')
            this.load()
          } catch (e) {
            Alert.error(e.errors ? e.errors.message : 'Error al desactivar')
          }
        })
    },
    scopeLabel(t) {
      const parts = [
        t.companyName,
        t.siteName && `sede ${t.siteName}`,
        t.areaName && `área ${t.areaName}`,
        t.positionName && `cargo ${t.positionName}`,
        t.contractTypeName && `contrato ${t.contractTypeName.toLowerCase()}`,
      ].filter(Boolean)
      return parts.length ? parts.join(' · ') : 'Global (todas)'
    },
    externalLabel(t) {
      const parts = [t.externalCode, t.externalVersion && `v. ${t.externalVersion}`].filter(Boolean)
      const approved = [t.approvedBy, t.approvedAt && this.formatDate(t.approvedAt)].filter(Boolean).join(', ')
      return (parts.join(' ') || '—') + (approved ? ` (aprobado: ${approved})` : '')
    },
    formatDate(d) {
      if (!d) return '—'
      const iso = String(d).substring(0, 10)
      const parts = iso.split('-')
      return parts.length === 3 ? `${parts[2]}/${parts[1]}/${parts[0]}` : iso
    },
  },
}
</script>
